import React, { useState, useEffect } from "react";
import "../CSS/updates.css";
import Sidebar from "./Sidebar";
import { FaSearch, FaUser, FaCalendarAlt, FaTag, FaFolder } from "react-icons/fa";
import { BASE_API_URL } from "../constants";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";

const Updates = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch blogs from backend
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${BASE_API_URL}/api/get-blogs`);
        const data = await response.json();
        setBlogs(data);
      } catch (error) {
        console.error(" Error fetching blogs:", error);
      }
    };
    fetchBlogs();
  }, []);

  // Utility: safely extract plain text from HTML (for previews)
  const htmlToPlainText = (html = "", limit = 200) => {
    // sanitize first
    const clean = DOMPurify.sanitize(html || "");
    // use a DOM parser in browser to extract textContent
    const tmp = document.createElement("div");
    tmp.innerHTML = clean;
    const text = tmp.textContent || tmp.innerText || "";
    if (limit && text.length > limit) return text.slice(0, limit) + "...";
    return text;
  };

  const parseListField = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return String(value)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  };

  // Search filter
  const filteredBlogs = blogs.filter(
    (blog) =>
      blog.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      // check HTML content as plain text
      htmlToPlainText(blog.content || "", 1000).toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.author?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="report-app-container">
        <main className="report-main-content">
          {/* Header */}
          <div className="report-page-header">
            <h1 className="report-page-title">
              <i className="fas fa-newspaper"></i> Updates & Blogs
            </h1>
            <div className="report-input-group">
              <input
                type="search"
                className="report-search-input"
                placeholder="Search blogs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="report-search-btn">
                <FaSearch />
              </button>
            </div>
          </div>

          {/* Blog Cards */}
          <div className="blogs-section">
            {filteredBlogs.length > 0 ? (
              filteredBlogs.map((blog) => (
                <div
                  className="blog-card"
                  key={blog.id}
                  onClick={() =>
                    navigate(
                      `/updates/${blog.id}/${encodeURIComponent(blog.title)}`
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  {/* Featured Image */}
                  {blog.featuredImage && (
                    <img
                      src={blog.featuredImage}
                      alt={blog.title}
                      className="blog-image"
                    />
                  )}

                  {/* Blog Content */}
                  <div className="blog-content">
                    {/* Title + Status */}
                    <div className="blog-header">
                      <h2 className="blog-title">{blog.title}</h2>
                      <span className={`blog-status ${blog.status?.toLowerCase()}`}>
                        {blog.status}
                      </span>
                    </div>

                    {/* Excerpt (render plain text so tags don't show) */}
                    {blog.excerpt && (
                      <p className="blog-excerpt">
                        <strong>Excerpt:</strong>{" "}
                        {htmlToPlainText(blog.excerpt, 160)}
                      </p>
                    )}

                    {/* Content Preview (plain text preview extracted from HTML) */}
                    {blog.content && (
                      <p className="blog-content-preview">
                        {htmlToPlainText(blog.content, 200)}
                      </p>
                    )}

                    {/* Blog Meta */}
                    <div className="blog-meta">
                      <span><FaUser /> {blog.author || "Unknown"}</span>
                      <span><FaCalendarAlt /> {new Date(blog.dateCreated).toLocaleDateString()}</span>
                      {blog.scheduleDate && (
                        <span>
                          <i className="fas fa-clock"></i>{" "}
                          {new Date(blog.scheduleDate).toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* Categories */}
                    {blog.categories && (
                      <div className="blog-categories">
                        <FaFolder />{" "}
                        {parseListField(blog.categories).map((cat, index) => (
                          <span key={index} className="category">
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Tags */}
                    {blog.tags && (
                      <div className="blog-tags">
                        <FaTag />{" "}
                        {parseListField(blog.tags).map((tag, index) => (
                          <span key={index} className="tag">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-blogs"> No blog posts found</p>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Updates;
